import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import { Category } from '@modules/common/types';
import { FeedbackListDrawer } from '@modules/feedback/components/FeedbackListDrawer';
import { useGetSelectedFeedbackMetadata } from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { LabelUpdateInProgressIcon } from '@modules/taxonomy/components/LabelUpdateInProgressIcon';
import { getInfoMessageForLabelState } from '@modules/taxonomy/helpers';
import { Box, List, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { LabelType } from 'generated/graphql';
import { useState } from 'react';
import { TimeRange } from 'v2/core/entities';
import { FieldNames } from 'v2/core/entities/fields';
import { QueryContainer } from 'v2/core/services/notebook/types';
import { combineWhereClauses } from 'v2/core/services/queryBuilder/combineWhereClauses';
import {
  OperationType,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';
import { getWhereClauseForFieldName } from 'v2/core/services/queryBuilder/whereClauseGenerator';
import getFieldNameToDisplay from 'v2/views/components/charts/tooltip/getFieldNameToDisplay';
import { DataListItem } from './DataListItem';

interface DataListProps<T> {
  baseWhereClause: WhereClause;
  dateRange: TimeRange;
  data: T[];
  fieldName: FieldNames;
  shouldShowAddToFilter?: boolean;
  shouldDisableInteraction?: boolean;
  onFeedbackDrawerClose?: () => void;
  shouldHideOpenInSearchOnFeedbackDrawer?: boolean;
}

interface DataShape {
  category?: Category;
  label: string;
  value?: number;
  id: string;
  variants?: Record<string, { label: string; value: number; id: string }>;
  isVisible?: boolean;
  isUpdateInProgress?: boolean;
}

export const DataList = ({
  baseWhereClause,
  dateRange,
  data,
  fieldName,
  shouldShowAddToFilter,
  shouldDisableInteraction,
  onFeedbackDrawerClose,
  shouldHideOpenInSearchOnFeedbackDrawer,
}: DataListProps<DataShape>) => {
  const theme = useTheme();

  const [activeData, setActiveData] = useState<DataShape>();
  const [shouldShowFeedbackDrawer, setShouldShowFeedbackDrawer] =
    useState(false);
  const [queryContainer, setQueryContainer] = useState<QueryContainer>();
  const shouldRefetch = useFeedbackOnFeedbackStore(
    ({ shouldRefetch }) => shouldRefetch,
  );

  const showFeedbackDrawer = (
    id: string,
    label: string,
    category?: Category,
    isUpdateInProgress?: boolean,
    isVisible?: boolean,
  ) => {
    if (!id || !label) {
      return;
    }
    setActiveData({ id, label, category, isUpdateInProgress, isVisible });
    const whereClause = getWhereClauseForFieldName({
      fieldName: fieldName,
      values: [id],
      op: OperationType.AND,
    });
    const feedbackQueryContainer = {
      AggregatePeriod: 2,
      DateRange: dateRange,
      WhereClause: combineWhereClauses(
        baseWhereClause,
        whereClause,
        OperationType.AND,
      ),
    };
    setQueryContainer(feedbackQueryContainer);
    setShouldShowFeedbackDrawer(true);
  };

  const closeFeedbackDrawer = () => {
    setShouldShowFeedbackDrawer(false);
    setQueryContainer(undefined);
    if (shouldRefetch) {
      onFeedbackDrawerClose?.();
    }
  };

  const selectedFeedbackMetadata = useGetSelectedFeedbackMetadata({
    whereClause: queryContainer?.WhereClause,
    labels: activeData
      ? [
          {
            id: activeData.id,
            fieldName: fieldName,
            name: activeData.label,
          },
        ]
      : undefined,
  });

  const category = activeData?.category;
  const shouldShowCategory = !!category;

  const isActiveDataInvisible = activeData?.isVisible === false;
  const isActiveDataUpdateInProgress =
    activeData?.isUpdateInProgress === true && !isActiveDataInvisible;

  return (
    <List sx={{ height: '100%', overflow: 'auto' }} disablePadding>
      {data.map((dataPoint) => (
        <DataListItem
          key={dataPoint.id}
          label={dataPoint.label}
          value={dataPoint.value}
          id={dataPoint.id}
          labelCategory={dataPoint.category}
          fieldName={fieldName}
          dateRange={dateRange}
          baseWhereClause={baseWhereClause}
          showFeedbackDrawer={showFeedbackDrawer}
          shouldShowAddToFilter={shouldShowAddToFilter}
          shouldDisableInteraction={shouldDisableInteraction}
          isVisible={dataPoint.isVisible}
          isUpdateInProgress={dataPoint.isUpdateInProgress}
        />
      ))}
      {queryContainer && (
        <FeedbackListDrawer
          open={shouldShowFeedbackDrawer}
          onClose={closeFeedbackDrawer}
          queryContainer={queryContainer}
          selectedFeedbackMetadata={selectedFeedbackMetadata}
          shouldHideOpenInSearch={shouldHideOpenInSearchOnFeedbackDrawer}
          header={{
            shouldEnableSort: true,
            renderTitle: () => {
              return (
                <Typography variant="caption">
                  Feedback with existing filters for
                </Typography>
              );
            },
            renderDescription: () => {
              return (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {fieldName && (
                    <Typography
                      variant="subtitle1"
                      color={
                        isActiveDataInvisible
                          ? 'text.disabled'
                          : 'text.secondary'
                      }
                    >
                      {getFieldNameToDisplay(fieldName)}:
                    </Typography>
                  )}
                  <Tooltip
                    arrow
                    title={
                      isActiveDataInvisible
                        ? getInfoMessageForLabelState({
                            labelState: 'invisible',
                            labelType: LabelType.Reason,
                          })
                        : null
                    }
                    placement="right"
                  >
                    <Stack direction="row" spacing={0.5} alignItems="baseline">
                      {shouldShowCategory && (
                        <Box>
                          <CategoryIcon
                            category={category}
                            size={18}
                            fill={
                              isActiveDataInvisible
                                ? theme.palette.text.disabled
                                : undefined
                            }
                          />
                        </Box>
                      )}
                      <Typography variant="subtitle1">
                        {activeData?.label ?? ''}
                      </Typography>
                      {isActiveDataUpdateInProgress && (
                        <Box>
                          <LabelUpdateInProgressIcon
                            size={22}
                            labelType={
                              FieldNames.Reason
                                ? LabelType.Reason
                                : LabelType.Aspect
                            }
                          />
                        </Box>
                      )}
                    </Stack>
                  </Tooltip>
                </Stack>
              );
            },
          }}
        />
      )}
    </List>
  );
};
